import { Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { FormHelpers, OnFormInitialValuesChange } from '../../../screens/utils';
import { journeyDataSelectors } from '../../../services/redux/features/journeyData.slice';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { AddressSearchFormField } from '../../Fieldset/AddressSearchFormField';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { PreviousAddressFormModel_MX } from './formModel';
import { getInitialValues } from './initialValues';
import { getValidationSchema } from './validationSchema';

type Props = {
  formHelpers: FormHelpers;
  isSecondHirer?: boolean;
};

export const PreviousAddressFormNew_MX: React.FC<Props> = ({ formHelpers, isSecondHirer }) => {
  const timeAtAddressTo =
    useSelector(isSecondHirer ? journeyDataSelectors.secondHirerAddress : journeyDataSelectors.address)
      ?.timeAtAddressFrom ?? '';
  const [initialValues, setInitialValues] = useState(() => getInitialValues(timeAtAddressTo));
  const [validationSchema] = useState<ObjectSchema<Partial<PreviousAddressFormModel_MX>>>(getValidationSchema);

  useEffect(() => {
    setInitialValues(getInitialValues(timeAtAddressTo));
  }, [timeAtAddressTo]);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (values: PreviousAddressFormModel_MX) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`addressForm:previousAddressForm:heading`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent
          onFormInitialValuesChange={onFormInitialValuesChange}
          disabled={isDisabledFormSummary}
          formTranslationKey="addressForm:currentAddressForm"
          formHelpers={formHelpers}
        />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  disabled: boolean;
  formTranslationKey: string;
  onFormInitialValuesChange: OnFormInitialValuesChange;
  formHelpers: FormHelpers;
};

export const FormContent = (props: FormContentProps) => {
  const { values, submitForm } = useFormikContext<PreviousAddressFormModel_MX>();
  const { formTranslationKey, disabled, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(formTranslationKey, disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(formTranslationKey, disableAllFormControls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formTranslationKey, disableAllFormControls]);

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">
            <AddressSearchFormField />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.addressLine1()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.addressLine2()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.addressLine3()}</Layout.Item>
        </Layout>
      </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.town()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.county()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.eirCode()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.residentialStatus()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">
              <Layout>
                <Layout.Item default="1/2" s="1/1">{inputs.timeAtAddressFrom(values.timeAtAddressTo)}</Layout.Item>
                <Layout.Item default="1/2" s="1/1">{inputs.timeAtAddressTo()}</Layout.Item>
              </Layout>
            </Layout.Item>
          </Layout>
        </Fieldset.Row>
       <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers} />
    </>
  );
};
